import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';

/**
 * Loading something for a page, done once and correctly.
 *
 * Every page used to write the same eight lines — a state, a loading flag,
 * an effect, a `.then`, a `.finally` — and none of them asked whether the
 * page was still there when the answer came back. Navigate away mid-fetch
 * and the state update landed on an unmounted component, which React
 * tolerates and nobody wants. This hook owns that: the answer is dropped
 * if the page has moved on or the inputs have changed, a refresh can be
 * asked for, a page can poll, and a search can be debounced.
 */
export interface LoadOptions<T> {
  /** The value before the first answer arrives. */
  initial: T;
  /** Load again when any of these change. */
  deps?: unknown[];
  /** Poll: reload every this many milliseconds while mounted. */
  every?: number;
  /** Wait this long after the inputs change before loading, for search boxes. */
  debounce?: number;
  onError?: (error: unknown) => void;
  /** Runs with each fresh answer, for form state seeded from it. */
  onLoaded?: (data: T) => void;
}

export interface Loaded<T> {
  data: T;
  setData: Dispatch<SetStateAction<T>>;
  /** True until the first answer, and false thereafter — a refresh is quiet. */
  loading: boolean;
  error: unknown;
  reload: () => void;
}

export function useLoad<T>(load: (signal: AbortSignal) => Promise<T>, options: LoadOptions<T>): Loaded<T> {
  const [data, setData] = useState<T>(options.initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(undefined);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);

  // The latest callbacks, without making them inputs: a page that passes a
  // fresh arrow function every render must not reload every render.
  const latest = useRef({ load, onError: options.onError, onLoaded: options.onLoaded });
  latest.current = { load, onError: options.onError, onLoaded: options.onLoaded };

  const deps = options.deps ?? [];
  const every = options.every ?? 0;
  const debounce = options.debounce ?? 0;

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const run = () => {
      latest.current
        .load(signal)
        .then((next) => {
          if (signal.aborted) return;
          setData(next);
          setError(undefined);
          latest.current.onLoaded?.(next);
        })
        .catch((err: unknown) => {
          if (signal.aborted || (err instanceof Error && err.name === 'AbortError')) return;
          setError(err);
          latest.current.onError?.(err);
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
        });
    };

    const first = setTimeout(run, debounce);
    const timer = every > 0 ? setInterval(run, every) : undefined;
    return () => {
      controller.abort();
      clearTimeout(first);
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, every, debounce, ...deps]);

  return { data, setData, loading, error, reload };
}
